import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import CustomEditor from "../../../admin-panel/custom/CustomEditor";
import { toast } from 'react-toastify';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';

const FormBlog = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validated, setValidated] = useState(false);
    const [initialDescription, setInitialDescription] = useState(isEdit ? null : '');

    const [formData, setFormData] = useState({
        blog_title: '',
        blog_desc: '',
        status: 'active',
        _is_most_viewed: false,
        category: '',
        meta_title: '',
        meta_desc: '',
        meta_keywords: ''
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const fetchBlogDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/blogs/${id}`);
            if (response.data.success) {
                const blog = response.data.data;
                setFormData({
                    blog_title: blog.blog_title || '',
                    blog_desc: blog.blog_desc || '',
                    status: blog.status || 'active',
                    _is_most_viewed: !!blog._is_most_viewed,
                    category: blog.category || '',
                    meta_title: blog.meta_title || '',
                    meta_desc: blog.meta_desc || '',
                    meta_keywords: blog.meta_keywords || ''
                });
                setInitialDescription(blog.blog_desc || '');
                if (blog.image_url) {
                    setImagePreview(blog.image_url);
                }
            } else {
                toast.error("Failed to load blog details");
                navigate('/superadmin/blogs');
            }
        } catch (error) {
            console.error('Error fetching blog details:', error);
            toast.error("Error loading blog details");
            navigate('/superadmin/blogs');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        if (isEdit) {
            fetchBlogDetails();
        }
    }, [isEdit, fetchBlogDetails]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDescriptionChange = (data) => {
        setFormData(prev => ({
            ...prev,
            blog_desc: data
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false || !formData.blog_desc.trim()) {
            e.stopPropagation();
            setValidated(true);
            if (!formData.blog_desc.trim()) {
                toast.error("Blog description is required");
            }
            return;
        }

        setSubmitting(true);

        const data = new FormData();
        data.append('blog_title', formData.blog_title);
        data.append('blog_desc', formData.blog_desc);
        data.append('status', formData.status);
        data.append('_is_most_viewed', formData._is_most_viewed);
        data.append('category', formData.category);
        data.append('meta_title', formData.meta_title);
        data.append('meta_desc', formData.meta_desc);
        data.append('meta_keywords', formData.meta_keywords);
        
        if (imageFile) {
            data.append('image', imageFile);
        }

        try {
            let response;
            if (isEdit) {
                response = await superAdminApiClient.put(`/superadmin/blogs/${id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await superAdminApiClient.post('/superadmin/blogs', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (response.data.success) {
                toast.success(isEdit ? "Blog updated successfully!" : "Blog created successfully!");
                navigate('/superadmin/blogs');
            } else {
                toast.error(response.data.message || "Something went wrong.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save blog");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-2">
            <Row className="justify-content-center">
                <Col lg={10}>
                    <div className="d-sm-flex align-items-center justify-content-between mb-4">
                        <h4 className="mb-sm-0 text-dark font-weight-bold">{isEdit ? 'Edit Blog' : 'Create New Blog'}</h4>
                        <div className="page-title-right">
                            <Link to="/superadmin/blogs" className="btn btn-outline-dark btn-sm">
                                <i className="ri-arrow-left-line align-bottom me-1"></i> Back to List
                            </Link>
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff' }}>
                        <Card.Body className="p-4 text-dark">
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Blog Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="blog_title"
                                                value={formData.blog_title}
                                                onChange={handleInputChange}
                                                placeholder="Enter a catchy blog title"
                                                required
                                                className="border-grey-subtle text-dark focus-ring"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please provide a blog title.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Description / Content <span className="text-danger">*</span></Form.Label>
                                            <div className="rich-editor-wrapper bg-white rounded text-dark">
                                                {(!isEdit || initialDescription !== null) && (
                                                    <CustomEditor 
                                                        data={formData.blog_desc}
                                                        onChange={handleDescriptionChange}
                                                        placeholder="Draft your blog content here..."
                                                    />
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                placeholder="e.g. Travel, Hotel, Guide"
                                                required
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Category is required.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Blog Banner Image</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <div className="mt-3 text-center border border-dashed rounded p-2" style={{ border: '1px dashed #ced4da', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                                                {imagePreview ? (
                                                    <img 
                                                        src={imagePreview} 
                                                        alt="Preview" 
                                                        className="img-fluid rounded" 
                                                        style={{ maxHeight: '150px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <span className="text-muted fs-12">No image selected</span>
                                                )}
                                            </div>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark font-medium">Settings</Form.Label>
                                            <div className="p-3 rounded d-flex flex-column gap-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                <Form.Check 
                                                    type="switch"
                                                    id="form-blog-status"
                                                    label="Active Status"
                                                    checked={formData.status === 'active'}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))}
                                                    style={{ cursor: 'pointer' }}
                                                    className="text-dark font-medium"
                                                />
                                                <Form.Check 
                                                    type="checkbox"
                                                    name="_is_most_viewed"
                                                    id="form-blog-most-viewed"
                                                    label="Mark as Most Viewed"
                                                    checked={formData._is_most_viewed}
                                                    onChange={handleInputChange}
                                                    style={{ cursor: 'pointer' }}
                                                    className="text-dark"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                                <h5 className="mb-3 text-primary font-weight-bold">SEO Meta Tags</h5>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_title"
                                                value={formData.meta_title}
                                                onChange={handleInputChange}
                                                placeholder="Enter meta title for search engines"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="meta_desc"
                                                value={formData.meta_desc}
                                                onChange={handleInputChange}
                                                placeholder="Enter meta description for search results"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Keywords</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_keywords"
                                                value={formData.meta_keywords}
                                                onChange={handleInputChange}
                                                placeholder="e.g. hotel booking, travel, luxury stays (comma separated)"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                                                Separate keywords with commas for best SEO results.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <Link to="/superadmin/blogs" className="btn btn-light border px-4">Cancel</Link>
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        className="px-4 d-flex align-items-center gap-1"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line fs-16"></i> {isEdit ? 'Save Changes' : 'Publish Blog'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FormBlog;
